import { useState, useEffect, useCallback } from 'react';

export interface PortfolioItem {
  title: string;
  location: string;
  category: string;
  image: string;
}

interface PortfolioLightboxProps {
  items: PortfolioItem[];
  lang?: 'en' | 'tr';
}

const LABELS = {
  en: { prev: 'Previous', next: 'Next', close: 'Close', of: 'of' },
  tr: { prev: 'Önceki', next: 'Sonraki', close: 'Kapat', of: '/' },
};

export default function PortfolioLightbox({ items, lang = 'en' }: PortfolioLightboxProps) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const t = LABELS[lang];

  const isOpen = activeIndex !== null;

  const close = useCallback(() => setActiveIndex(null), []);

  const prev = useCallback(() => {
    setActiveIndex((i) => (i === null ? null : (i - 1 + items.length) % items.length));
  }, [items.length]);

  const next = useCallback(() => {
    setActiveIndex((i) => (i === null ? null : (i + 1) % items.length));
  }, [items.length]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, close, prev, next]);

  const current = activeIndex !== null ? items[activeIndex] : null;

  return (
    <>
      {/* Portfolio Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {items.map((project, index) => (
          <div
            key={index}
            className="group relative overflow-hidden rounded-xl bg-gray-900 hover:scale-105 transition-all duration-300 border border-gray-800 cursor-pointer"
            onClick={() => setActiveIndex(index)}
          >
            <div className="relative overflow-hidden">
              <img
                src={project.image}
                alt={project.title}
                className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 w-12 h-12 flex items-center justify-center rounded-full bg-yellow-400">
                  <i className="ri-zoom-in-line text-black text-xl"></i>
                </div>
              </div>
            </div>
            <div className="p-6 bg-gray-900">
              <div className="bg-gray-800 text-gray-300 text-xs font-bold px-3 py-1 rounded-full inline-block mb-3 uppercase tracking-wide">
                {project.category}
              </div>
              <h3 className="text-yellow-400 font-bold text-xl mb-2 leading-tight">{project.title}</h3>
              <div className="flex items-center text-gray-400 text-xs font-medium">
                <i className="ri-map-pin-line mr-2 text-yellow-400"></i>
                {project.location}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Lightbox */}
      {isOpen && current && (
        <div
          className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={close}
        >
          <div
            className="relative max-w-5xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close */}
            <button
              onClick={close}
              aria-label={t.close}
              className="absolute -top-12 right-0 w-10 h-10 flex items-center justify-center text-white hover:text-yellow-400 transition-colors cursor-pointer z-10"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>

            {/* Counter */}
            <div className="absolute -top-12 left-0 text-gray-400 text-sm">
              {(activeIndex ?? 0) + 1} {t.of} {items.length}
            </div>

            {/* Image */}
            <div className="relative rounded-xl overflow-hidden bg-gray-900">
              <img
                src={current.image}
                alt={current.title}
                className="w-full max-h-[70vh] object-contain bg-black"
              />

              {/* Bottom info bar */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent px-6 py-5">
                <span className="bg-yellow-400/20 border border-yellow-400/40 text-yellow-400 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                  {current.category}
                </span>
                <h3 className="text-white font-bold text-xl mt-2 leading-tight">{current.title}</h3>
                <div className="flex items-center text-gray-300 text-sm mt-1">
                  <i className="ri-map-pin-line mr-2 text-yellow-400"></i>
                  {current.location}
                </div>
              </div>
            </div>

            {/* Prev / Next */}
            <button
              onClick={prev}
              aria-label={t.prev}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-black/60 hover:bg-yellow-400 border border-gray-600 hover:border-yellow-400 rounded-full text-white hover:text-black transition-all duration-300 cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-xl"></i>
            </button>
            <button
              onClick={next}
              aria-label={t.next}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-black/60 hover:bg-yellow-400 border border-gray-600 hover:border-yellow-400 rounded-full text-white hover:text-black transition-all duration-300 cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>

            {/* Thumbnail strip */}
            <div className="flex gap-2 mt-4 overflow-x-auto pb-1 justify-center">
              {items.map((item, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIndex(i)}
                  className={`flex-shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 transition-all duration-200 cursor-pointer ${
                    i === activeIndex ? 'border-yellow-400 opacity-100' : 'border-transparent opacity-50 hover:opacity-80'
                  }`}
                >
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
